import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, CalendarRange, Printer, Download, ArrowLeft, Ban, RotateCcw, Receipt, CreditCard, Landmark, FileWarning, Wallet } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { money, shortDate } from "@/lib/format";
import { Card } from "@/components/ui-kit/Card";
import { Button } from "@/components/ui-kit/Button";
import { Select } from "@/components/ui-kit/Select";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui-kit/Table";
import { Pill } from "@/components/ui-kit/Pill";
import { WriteOffDialog } from "@/components/invoices/WriteOffDialog";
import { useFunder } from "@/funder/FunderContext";

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function csvEscape(v) {
  const s = v === null || v === undefined ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(filename, lines) {
  const csv = lines.map((row) => row.map(csvEscape).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

function SectionTitle({ icon: Icon, children, sub }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="grid h-9 w-9 place-items-center rounded-lg bg-primary/10 text-primary"><Icon className="h-5 w-5" /></span>
      <div>
        <h2 className="text-lg font-semibold text-foreground">{children}</h2>
        {sub && <p className="text-sm text-muted-foreground">{sub}</p>}
      </div>
    </div>
  );
}

function StatCard({ label, value, accent, testid }) {
  return (
    <Card className="p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`nums mt-1 text-xl font-semibold ${accent || "text-foreground"}`} data-testid={testid}>{value}</p>
    </Card>
  );
}

export function YearEndReportPage() {
  const navigate = useNavigate();
  const { abbr, payerLabel } = useFunder();
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [woInvoice, setWoInvoice] = useState(null);

  const load = useCallback(() => {
    setData(null);
    return api.get("/reports/year-end", { params: { year } })
      .then((r) => setData(r.data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const clearWriteOff = async (invoiceId) => {
    try { await api.post(`/invoices/${invoiceId}/write-off`, { written_off: false }); await load(); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const exportCsv = () => {
    if (!data) return;
    const F = abbr;
    const L = [];
    L.push([`MatterTab — Cash-Basis Year-End Report ${data.year}`]);
    L.push([`Generated ${shortDate(data.generated_at)}`, "Cash basis: income = payments received, not billed."]);
    L.push([]);
    L.push(["INCOME COLLECTED (cash received)"]);
    L.push(["Payer", "Amount"]);
    L.push(["Client", data.income.by_payer.client]);
    L.push([F, data.income.by_payer.funder]);
    L.push(["Total", data.income.total]);
    L.push([]);
    L.push(["By method", "Amount"]);
    Object.entries(data.income.by_method).forEach(([k, v]) => L.push([k, v]));
    L.push([]);
    L.push(["By quarter", "Amount"]);
    Object.entries(data.income.by_quarter).forEach(([k, v]) => L.push([k, v]));
    L.push([]);
    L.push(["By month", "Amount"]);
    data.income.by_month.forEach((m) => L.push([MONTH_LABELS[parseInt(m.month, 10) - 1], m.amount]));
    L.push([]);
    L.push(["By client / matter", "Client", F, "Total"]);
    data.income.by_matter.forEach((r) => L.push([`${r.client_name} — ${r.matter_name}`, r.client, r.funder, r.total]));
    L.push([]);
    L.push(["1099 RECONCILIATION HELPERS"]);
    L.push(["Total received by Card (for 1099-K)", data.helpers_1099.card_1099k]);
    L.push([`Total received from ${F}`, data.helpers_1099.acsa]);
    L.push([]);
    L.push(["BILLED vs COLLECTED + YEAR-END A/R"]);
    L.push(["Client / Matter", "Status", "Billed (to date)", "Collected (to date)", "A/R (net)", "Credit"]);
    data.billed_vs_collected.rows.forEach((r) => L.push([`${r.client_name} — ${r.matter_name}`, r.matter_status, r.billed, r.collected, r.ar, r.credit]));
    L.push(["TOTAL", "", data.billed_vs_collected.billed_total, data.billed_vs_collected.collected_total, data.billed_vs_collected.ar_total, data.billed_vs_collected.credits_total]);
    L.push([]);
    L.push(["Reconciliation", "Billed - Collected = A/R - Credits",
      `${data.billed_vs_collected.billed_total} - ${data.billed_vs_collected.collected_total} = ${data.billed_vs_collected.ar_total} - ${data.billed_vs_collected.credits_total}`]);
    L.push([]);
    L.push(["A/R aging", "Amount", "Count"]);
    data.billed_vs_collected.aging.forEach((b) => L.push([`${b.bucket} days`, b.amount, b.count]));
    L.push([]);
    L.push(["BILLED BUT UNPAID (open invoices)"]);
    L.push(["Invoice", "Client / Matter", "Payer", "Issued", "Age (days)", "Balance due"]);
    data.unpaid.rows.forEach((r) => L.push([r.number, `${r.client_name} — ${r.matter_name}`, payerLabel(r.payer), shortDate(r.issue_date), r.age_days, r.balance_due]));
    L.push(["TOTAL UNPAID", "", "", "", "", data.unpaid.total]);
    L.push([]);
    L.push(["WRITTEN OFF (uncollectible)"]);
    L.push(["Invoice", "Client / Matter", "Payer", "Written off", "Reason", "Balance"]);
    data.written_off.rows.forEach((r) => L.push([r.number, `${r.client_name} — ${r.matter_name}`, payerLabel(r.payer), shortDate(r.written_off_at), r.write_off_reason || "", r.balance_due]));
    L.push(["TOTAL WRITTEN OFF", "", "", "", "", data.written_off.total]);
    L.push([]);
    L.push(["ADVANCED COSTS"]);
    L.push(["Total advanced", data.advanced_costs.total]);
    L.push(["Reimbursed", data.advanced_costs.reimbursed]);
    L.push(["Unreimbursed (still owed back)", data.advanced_costs.unreimbursed]);
    Object.entries(data.advanced_costs.by_category).forEach(([k, v]) => L.push([`  ${k}`, v]));
    downloadCsv(`year-end-report-${data.year}.csv`, L);
  };

  if (error && !data) return <Card><div className="p-6 text-sm text-destructive" data-testid="year-end-error">{error}</div></Card>;
  if (data === null) return <div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const inc = data.income;
  const bvc = data.billed_vs_collected;

  return (
    <div className="space-y-8" data-testid="year-end-page">
      <button onClick={() => navigate("/reports")} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground print:hidden" data-testid="year-end-back">
        <ArrowLeft className="h-4 w-4" /> All reports
      </button>

      {/* Header + controls */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-lg bg-primary/10 text-primary print:hidden"><CalendarRange className="h-5 w-5" /></span>
          <div>
            <h1 className="font-serif text-3xl font-semibold tracking-tight text-foreground">Year-End Report · {data.year}</h1>
            <p className="mt-1 text-sm text-muted-foreground">Cash basis — income is money <span className="font-medium text-foreground">received</span>, not billed. A read-only summary for tax prep.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 print:hidden">
          <Select value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} data-testid="year-end-year" className="max-w-[110px]">
            {data.available_years.map((y) => <option key={y} value={y}>{y}</option>)}
          </Select>
          <Button variant="secondary" size="sm" onClick={() => window.print()} data-testid="year-end-print"><Printer className="h-4 w-4" /> Print</Button>
          <Button variant="secondary" size="sm" onClick={exportCsv} data-testid="year-end-csv"><Download className="h-4 w-4" /> CSV</Button>
        </div>
      </div>

      <div className="rounded-md border border-amber-300/40 bg-amber-50 px-4 py-2.5 text-xs text-amber-900 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200" data-testid="year-end-disclaimer">
        For tax-prep reference only. Figures are read from the validated ledger on a cash basis and are not a substitute for professional accounting advice. Generated {shortDate(data.generated_at)}.
      </div>

      {/* 1. Income collected */}
      <section className="space-y-4">
        <SectionTitle icon={Receipt} sub="Non-void payments received this year, each counted once. Excludes unearned retainer deposits.">Income collected</SectionTitle>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard label="Total collected" value={money(inc.total)} testid="ye-income-total" />
          <StatCard label="From Client" value={money(inc.by_payer.client)} testid="ye-income-client" />
          <StatCard label={`From ${abbr}`} value={money(inc.by_payer.funder)} testid="ye-income-funder" />
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <Card className="p-5">
            <p className="text-sm font-medium text-foreground">By method</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {Object.entries(inc.by_method).length === 0 ? <span className="text-sm text-muted-foreground">No payments this year.</span>
                : Object.entries(inc.by_method).map(([k, v]) => <Pill key={k} tone="muted" data-testid={`ye-method-${k}`}>{k}: {money(v)}</Pill>)}
            </div>
            <p className="mt-5 text-sm font-medium text-foreground">By quarter</p>
            <div className="mt-3 grid grid-cols-4 gap-2">
              {Object.entries(inc.by_quarter).map(([q, v]) => (
                <div key={q} className="rounded-md border border-border bg-secondary/30 px-2 py-2 text-center">
                  <p className="text-[11px] text-muted-foreground">{q}</p>
                  <p className="nums text-sm font-semibold text-foreground" data-testid={`ye-quarter-${q}`}>{money(v)}</p>
                </div>
              ))}
            </div>
          </Card>
          <Card className="p-5">
            <p className="text-sm font-medium text-foreground">By month</p>
            <div className="mt-3 grid grid-cols-6 gap-2">
              {inc.by_month.map((m) => {
                const idx = parseInt(m.month, 10) - 1;
                return (
                  <div key={m.month} className={`rounded-md border px-1.5 py-2 text-center ${m.amount > 0 ? "border-primary/30 bg-primary/5" : "border-border bg-secondary/20"}`}>
                    <p className="text-[10px] text-muted-foreground">{MONTH_LABELS[idx]}</p>
                    <p className="nums text-xs font-medium text-foreground">{m.amount > 0 ? money(m.amount) : "—"}</p>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
        {inc.by_matter.length > 0 && (
          <Card>
            <Table>
              <THead><TR><TH>Client / Matter</TH><TH className="text-right">Client</TH><TH className="text-right">{abbr}</TH><TH className="text-right">Total</TH></TR></THead>
              <TBody>
                {inc.by_matter.map((r) => (
                  <TR key={r.matter_id} data-testid={`ye-income-matter-${r.matter_id}`}>
                    <TD className="text-foreground">{r.client_name} <span className="text-muted-foreground">· {r.matter_name}</span></TD>
                    <TD className="nums text-right text-muted-foreground">{money(r.client)}</TD>
                    <TD className="nums text-right text-muted-foreground">{money(r.funder)}</TD>
                    <TD className="nums text-right font-medium text-foreground">{money(r.total)}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </Card>
        )}
      </section>

      {/* 2. 1099 helpers */}
      <section className="space-y-4">
        <SectionTitle icon={CreditCard} sub="Quick totals to reconcile against the 1099 forms you receive.">1099 reconciliation helpers</SectionTitle>
        <div className="grid gap-4 sm:grid-cols-2">
          <Card className="flex items-center gap-4 p-5">
            <span className="grid h-11 w-11 place-items-center rounded-lg bg-primary/10 text-primary"><CreditCard className="h-5 w-5" /></span>
            <div>
              <p className="text-sm text-muted-foreground">Received by Card (for 1099-K)</p>
              <p className="nums text-2xl font-semibold text-foreground" data-testid="ye-1099-card">{money(data.helpers_1099.card_1099k)}</p>
            </div>
          </Card>
          <Card className="flex items-center gap-4 p-5">
            <span className="grid h-11 w-11 place-items-center rounded-lg bg-status-paid/10 text-status-paid"><Landmark className="h-5 w-5" /></span>
            <div>
              <p className="text-sm text-muted-foreground">Received from {abbr}</p>
              <p className="nums text-2xl font-semibold text-foreground" data-testid="ye-1099-acsa">{money(data.helpers_1099.acsa)}</p>
            </div>
          </Card>
        </div>
      </section>

      {/* 3. Billed vs collected + A/R */}
      <section className="space-y-4">
        <SectionTitle icon={Wallet} sub="Lifetime billed vs collected, with the outstanding balance (A/R) at year end.">Billed vs. collected & year-end A/R</SectionTitle>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard label="Billed (to date)" value={money(bvc.billed_total)} testid="ye-billed-total" />
          <StatCard label="Collected (to date)" value={money(bvc.collected_total)} testid="ye-collected-total" />
          <StatCard label="A/R outstanding" value={money(bvc.ar_total)} accent="text-status-void" testid="ye-ar-total" />
          <StatCard label="Credits / overpayments" value={money(bvc.credits_total)} accent="text-status-paid" testid="ye-credits-total" />
        </div>
        <div className="rounded-md border border-border bg-secondary/30 px-4 py-2.5 text-sm text-foreground" data-testid="ye-reconcile">
          <span className="nums font-medium">{money(bvc.billed_total)}</span> billed
          <span className="text-muted-foreground"> − </span>
          <span className="nums font-medium">{money(bvc.collected_total)}</span> collected
          <span className="text-muted-foreground"> − </span>
          <span className="nums font-medium">{money(bvc.written_off_total || 0)}</span> written-off
          <span className="text-muted-foreground"> = </span>
          <span className="nums font-medium">{money(bvc.ar_total)}</span> A/R
          <span className="text-muted-foreground"> − </span>
          <span className="nums font-medium">{money(bvc.credits_total)}</span> credits
          <span className="ml-2 text-xs text-muted-foreground">(balances net of payments received; written-off excluded from A/R)</span>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {bvc.aging.map((b) => (
            <Card key={b.bucket} className="p-4">
              <p className="text-xs text-muted-foreground">{b.bucket} days <span className="text-muted-foreground/70">({b.count})</span></p>
              <p className="nums mt-1 text-lg font-semibold text-foreground" data-testid={`ye-aging-${b.bucket}`}>{money(b.amount)}</p>
            </Card>
          ))}
        </div>
        {bvc.rows.length > 0 && (
          <Card>
            <Table>
              <THead><TR><TH>Client / Matter</TH><TH>Status</TH><TH className="text-right">Billed</TH><TH className="text-right">Collected</TH><TH className="text-right">A/R</TH><TH className="text-right">Credit</TH></TR></THead>
              <TBody>
                {bvc.rows.map((r) => (
                  <TR key={r.matter_id} data-testid={`ye-bvc-${r.matter_id}`}>
                    <TD className="text-foreground">{r.client_name} <span className="text-muted-foreground">· {r.matter_name}</span></TD>
                    <TD className="text-muted-foreground">{r.matter_status}</TD>
                    <TD className="nums text-right text-muted-foreground">{money(r.billed)}</TD>
                    <TD className="nums text-right text-muted-foreground">{money(r.collected)}</TD>
                    <TD className="nums text-right font-medium text-foreground">{money(r.ar)}</TD>
                    <TD className="nums text-right text-status-paid">{r.credit > 0 ? money(r.credit) : "—"}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </Card>
        )}
      </section>

      {/* 4. Billed but unpaid / written off */}
      <section className="space-y-4">
        <SectionTitle icon={FileWarning} sub="Open invoices still owed at year end. Mark uncollectible ones written off — frozen amounts never change.">Billed but unpaid &amp; written off</SectionTitle>
        <Card>
          <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
            <p className="text-sm font-medium text-foreground">Unpaid invoices</p>
            <p className="text-sm text-muted-foreground">Total: <span className="nums font-semibold text-foreground" data-testid="ye-unpaid-total">{money(data.unpaid.total)}</span></p>
          </div>
          {data.unpaid.rows.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">Nothing unpaid — all caught up.</div>
          ) : (
            <Table>
              <THead><TR><TH>Invoice</TH><TH>Client / Matter</TH><TH>Payer</TH><TH>Issued</TH><TH className="text-right">Age</TH><TH className="text-right">Balance</TH><TH className="print:hidden" /></TR></THead>
              <TBody>
                {data.unpaid.rows.map((r) => (
                  <TR key={r.invoice_id} data-testid={`ye-unpaid-${r.invoice_id}`}>
                    <TD><button className="text-foreground hover:underline" onClick={() => navigate(`/invoices/${r.invoice_id}`)}>{r.number}</button></TD>
                    <TD className="text-muted-foreground">{r.client_name} · {r.matter_name}</TD>
                    <TD className="text-muted-foreground">{payerLabel(r.payer)}</TD>
                    <TD className="whitespace-nowrap text-muted-foreground">{shortDate(r.issue_date)}</TD>
                    <TD className="nums text-right text-muted-foreground">{r.age_days}d</TD>
                    <TD className="nums text-right font-medium text-foreground">{money(r.balance_due)}</TD>
                    <TD className="text-right print:hidden">
                      <Button size="sm" variant="ghost" onClick={() => setWoInvoice({ ...r, id: r.invoice_id })} data-testid={`ye-writeoff-${r.invoice_id}`}>
                        <Ban className="h-3.5 w-3.5" /> Write off
                      </Button>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </Card>

        {data.written_off.rows.length > 0 && (
          <Card>
            <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
              <p className="text-sm font-medium text-foreground">Written off (uncollectible)</p>
              <p className="text-sm text-muted-foreground">Total: <span className="nums font-semibold text-status-void" data-testid="ye-writtenoff-total">{money(data.written_off.total)}</span></p>
            </div>
            <Table>
              <THead><TR><TH>Invoice</TH><TH>Client / Matter</TH><TH>Written off</TH><TH>Reason</TH><TH className="text-right">Balance</TH><TH className="print:hidden" /></TR></THead>
              <TBody>
                {data.written_off.rows.map((r) => (
                  <TR key={r.invoice_id} data-testid={`ye-writtenoff-${r.invoice_id}`}>
                    <TD><button className="text-foreground hover:underline" onClick={() => navigate(`/invoices/${r.invoice_id}`)}>{r.number}</button></TD>
                    <TD className="text-muted-foreground">{r.client_name} · {r.matter_name}</TD>
                    <TD className="whitespace-nowrap text-muted-foreground">{shortDate(r.written_off_at)}</TD>
                    <TD className="text-muted-foreground">{r.write_off_reason || "—"}</TD>
                    <TD className="nums text-right font-medium text-status-void">{money(r.balance_due)}</TD>
                    <TD className="text-right print:hidden">
                      <Button size="sm" variant="ghost" onClick={() => clearWriteOff(r.invoice_id)} data-testid={`ye-undo-writeoff-${r.invoice_id}`}>
                        <RotateCcw className="h-3.5 w-3.5" /> Undo
                      </Button>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </Card>
        )}
      </section>

      {/* 5. Advanced costs */}
      <section className="space-y-4">
        <SectionTitle icon={Receipt} sub="Costs the firm advanced on matters (filing fees, service, experts), from the costs ledger.">Advanced costs</SectionTitle>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard label="Total advanced" value={money(data.advanced_costs.total)} testid="ye-costs-total" />
          <StatCard label="Reimbursed" value={money(data.advanced_costs.reimbursed)} accent="text-status-paid" testid="ye-costs-reimbursed" />
          <StatCard label="Unreimbursed (owed back)" value={money(data.advanced_costs.unreimbursed)} accent="text-status-void" testid="ye-costs-unreimbursed" />
        </div>
        {Object.keys(data.advanced_costs.by_category).length > 0 && (
          <div className="flex flex-wrap gap-2">
            {Object.entries(data.advanced_costs.by_category).map(([k, v]) => <Pill key={k} tone="muted">{k}: {money(v)}</Pill>)}
          </div>
        )}
      </section>

      <WriteOffDialog open={!!woInvoice} onOpenChange={(o) => { if (!o) setWoInvoice(null); }} invoice={woInvoice} onDone={load} />
    </div>
  );
}

export default YearEndReportPage;
